import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Zap, ArrowRight, Lock } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_revenue-flow-14/artifacts/7y9pwb5v_ChatGPT%20Image%20Jan%2023%2C%202026%20at%2012_25_42%20PM.png";

const unlockedNavItems = [
  { id: 'nav-problem', label: 'Problem', href: '#problem' },
  { id: 'nav-solution', label: 'Solution', href: '#solution' },
  { id: 'nav-services', label: 'Services', href: '#services' },
  { id: 'nav-contact', label: 'Contact', href: '#contact' },
];

const lockedNavItems = [
  { id: 'nav-unlock', label: 'Unlock Guide', href: '#unlock-guide' },
];

// Extracted mobile menu component
function MobileMenu({ isOpen, navItems, isUnlocked, onClose, onNavigate }) {
  if (!isOpen) return null;
  
  return (
    <motion.div
      data-testid="mobile-menu"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-40 bg-[#030303]/95 backdrop-blur-xl pt-24 md:hidden"
    >
      <div className="container-custom flex flex-col gap-6 py-8">
        {navItems.map((item, index) => (
          <motion.button
            key={item.id}
            data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(' ', '-')}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => onNavigate(item.href)}
            className="text-2xl font-semibold text-white text-left py-3 border-b border-white/10"
          >
            {item.label}
          </motion.button>
        ))}
        
        <motion.button
          data-testid="mobile-cta-button"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={() => {
            onClose();
            if (isUnlocked) {
              onNavigate('#contact');
            } else {
              setTimeout(() => {
                const formElement = document.querySelector('[data-testid="gate-first-name"]');
                if (formElement) {
                  formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 100);
            }
          }}
          className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
        >
          {isUnlocked ? (
            <>
              <Zap className="w-5 h-5" />
              <span>Book a Strategy Call</span>
            </>
          ) : (
            <>
              <Lock className="w-5 h-5" />
              <span>Unlock the Guide</span>
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
}

export default function Navbar({ isUnlocked }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const navItems = isUnlocked ? unlockedNavItems : lockedNavItems;
  
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);
  
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  
  const scrollToSection = useCallback((href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  }, []);

  const handleUnlockClick = useCallback(() => {
    const formElement = document.querySelector('[data-testid="gate-first-name"]');
    if (formElement && window.innerWidth < 1024) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      scrollToSection('#unlock-guide');
    }
  }, [scrollToSection]);
  
  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'py-3' : 'py-6'
        }`}
      >
        <div className="container-custom">
          <div className={`flex items-center justify-between px-6 py-3 rounded-full transition-all duration-500 ${
            isScrolled ? 'glass-heavy' : ''
          }`}>
            <motion.a
              data-testid="navbar-logo"
              href="#"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <img src={LOGO_URL} alt="Zahar Lab" className="h-10 md:h-12 w-auto" />
            </motion.a>
            
            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <motion.button
                  key={item.id}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => scrollToSection(item.href)}
                  className="text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-widest"
                  whileHover={{ y: -2 }}
                >
                  {item.label}
                </motion.button>
              ))}
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              {isUnlocked ? (
                <motion.button
                  data-testid="nav-cta-button"
                  onClick={() => scrollToSection('#contact')}
                  className="btn-primary flex items-center gap-2 text-sm py-3 px-6"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Book a Call</span>
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              ) : (
                <motion.button
                  data-testid="nav-unlock-button"
                  onClick={handleUnlockClick}
                  className="btn-primary flex items-center gap-2 text-sm py-3 px-6"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Lock className="w-4 h-4" />
                  <span>Get Access</span>
                </motion.button>
              )}
            </div>
            
            <motion.button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileTap={{ scale: 0.9 }}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.button>
          </div>
        </div>
      </motion.nav>
      
      <AnimatePresence>
        <MobileMenu
          isOpen={isMobileMenuOpen}
          navItems={navItems}
          isUnlocked={isUnlocked}
          onClose={() => setIsMobileMenuOpen(false)}
          onNavigate={scrollToSection}
        />
      </AnimatePresence>
    </>
  );
}
